import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { queryKeys } from '@/lib/api/queryKeys';
import type { EventFacts, EventFactsLedgerRow } from '@/features/insights/factsApi';
import { fmtInt, fmtUsd } from '@/features/insights/engine';
import { movementsApi, type DispositionKind } from './movementsApi';
import { useWarehouse } from './useWarehouse';
import type { XkStock } from '@/lib/api/types';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { usePagination } from '@/lib/hooks/usePagination';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';

/**
 * My Merch como almacén (maqueta «Xotski Dashboards», sección My Merch):
 *
 *   1. «Qué hay en el almacén»: cartera, comprometido, reposición, sin destino,
 *      y la acción prioritaria (P1 costes · P2 reasignar · P3 recuentos).
 *   2. Lotes sin destino que necesitan una disposición (por evento cerrado y
 *      premio): entregado fuera de estación, uso de operaciones, merma,
 *      recuento. Cada una escribe una fila nueva; el libro es «append only».
 *   3. Movimientos recientes: el libro, derivado en el servidor.
 *
 * Todo sale de los mismos hechos que el informe de cada evento.
 */
export function WarehousePanel({
  brandId,
  stocks,
  onEditStock,
}: {
  brandId: number | null;
  stocks: XkStock[];
  onEditStock: (stock: XkStock) => void;
}) {
  const t = useT();
  const { model, factsQ, movementsQ } = useWarehouse(brandId, stocks);

  const movements = movementsQ.data ?? [];
  const pct = model.owned > 0 ? Math.round((model.committed / model.owned) * 1000) / 10 : 0;

  // Acción prioritaria: P1 costes → P2 reasignar → P3 recuentos → nada.
  const action = model.missingCost.length
    ? { p: 1, text: t('stock.warehouse.action.costs', { k: model.missingCost.length, n: stocks.length }), cta: t('stock.warehouse.action.costsCta'), go: () => onEditStock(model.missingCost[0]!), rest: model.missingCost.length - 1 }
    : model.restock.some((r) => r.headroom <= 0)
      ? { p: 2, text: t('stock.warehouse.action.reallocate', { name: model.restock[0]!.s.name }), cta: t('stock.warehouse.action.reallocateCta'), to: '/brand-inventory', rest: model.restock.length - 1 }
      : model.unclosedEnded.length
        ? { p: 3, text: t('stock.warehouse.action.counts', { n: model.unclosedEnded.length }), cta: t('stock.warehouse.action.countsCta'), to: brandId ? `/brand/${brandId}/leftovers` : '/stock', rest: model.unclosedEnded.length - 1 }
        : null;

  const tiles = [
    { key: 'portfolio', label: t('stock.warehouse.portfolio'), value: fmtInt(model.owned), hint: model.ownedValue == null ? t('stock.warehouse.portfolioPending', { n: stocks.length, k: model.missingCost.length }) : t('stock.warehouse.portfolioHint', { n: stocks.length, value: fmtUsd(model.ownedValue) }), tone: 'lime' as const, chip: null },
    { key: 'committed', label: t('stock.warehouse.committed'), value: fmtInt(model.committed), hint: t('stock.warehouse.committedHint', { p: pct, n: model.openEvents }), tone: 'plain' as const, chip: null },
    { key: 'restock', label: t('stock.warehouse.restock'), value: String(model.restock.length), hint: model.restock[0] ? t('stock.warehouse.restockHint', { name: model.restock[0].s.name }) : t('stock.warehouse.restockNone'), tone: model.restock.length ? ('amber' as const) : ('plain' as const), chip: model.restock.length ? t('stock.warehouse.chipAction') : null },
    { key: 'unaccounted', label: t('stock.warehouse.unaccounted'), value: fmtInt(model.unaccounted), hint: t('stock.warehouse.unaccountedHint', { value: model.unaccountedValue == null ? t('stock.warehouse.costPending') : fmtUsd(model.unaccountedValue), last: model.lastClosing ? `${dayjs(model.lastClosing.at).format('MMM D')} · ${model.lastClosing.name}` : t('stock.warehouse.never') }), tone: 'amber' as const, chip: model.unaccounted ? t('stock.warehouse.chipDisposition') : null },
  ];

  return (
    <>
      {/* ── 1 · Qué hay en el almacén ── */}
      <section
        data-testid="warehouse-hero"
        className="rounded-2xl p-5 text-white"
        style={{ background: 'var(--color-dash-night)', border: '1px solid var(--color-dash-night-border, #242424)' }}
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-bold">{t('stock.warehouse.title')}</h2>
          <span className="dash-mono rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest" style={{ background: 'rgba(200,229,48,0.12)', color: 'var(--color-dash-lime)' }}>
            {t('stock.warehouse.ledgerChip', { n: movements.length })}
          </span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {tiles.map((tile) => (
            <div
              key={tile.key}
              className="rounded-xl p-4"
              style={{
                background: tile.tone === 'lime' ? '#1B1E0C' : '#141414',
                border: `1px solid ${tile.tone === 'lime' ? '#3A4315' : tile.tone === 'amber' ? '#4A3A12' : '#242424'}`,
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="dash-mono text-[10px] font-bold uppercase tracking-widest" style={{ color: tile.tone === 'lime' ? 'var(--color-dash-lime)' : tile.tone === 'amber' ? 'var(--color-dash-amber)' : '#9A9A94' }}>
                  {tile.label}
                </span>
                {tile.chip ? (
                  <span className="dash-mono rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest" style={{ background: 'rgba(251,191,36,0.18)', color: 'var(--color-dash-amber)' }}>
                    {tile.chip}
                  </span>
                ) : null}
              </div>
              <div className="dash-mono mt-2 text-[38px] font-bold leading-none tabular-nums">{tile.value}</div>
              <div className="mt-2 text-xs" style={{ color: '#9A9A94' }}>{tile.hint}</div>
            </div>
          ))}
        </div>
        {action ? (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl px-4 py-3" style={{ background: '#141414', border: '1px solid #242424' }}>
            <span className="dash-mono grid h-7 w-7 place-items-center rounded-full text-[11px] font-bold" style={{ background: action.p === 1 ? 'var(--color-dash-lime)' : 'var(--color-dash-amber)', color: '#111' }}>
              P{action.p}
            </span>
            <span className="flex-1 text-sm">{action.text}</span>
            <span className="text-xs" style={{ color: '#9A9A94' }}>
              {action.rest > 0 ? t('stock.warehouse.action.moreWaiting', { n: action.rest }) : t('stock.warehouse.action.nothingElse')}
            </span>
            {'to' in action && action.to ? (
              <Link to={action.to} className="rounded-full px-4 py-2 text-sm font-bold" style={{ background: 'var(--color-dash-lime)', color: '#111' }}>
                {action.cta}
              </Link>
            ) : (
              <button type="button" onClick={'go' in action ? action.go : undefined} className="rounded-full px-4 py-2 text-sm font-bold" style={{ background: 'var(--color-dash-lime)', color: '#111' }}>
                {action.cta}
              </button>
            )}
          </div>
        ) : null}
      </section>

      {/* ── 2 · Lotes sin destino ── */}
      {factsQ.data && (model.lots.length > 0 || movements.some((m) => m.kind === 'disposition')) ? (
        <DispositionLots lots={model.lots} onDone={() => { void factsQ.refetch(); void movementsQ.refetch(); }} />
      ) : null}
    </>
  );
}

// ── Lotes sin destino ────────────────────────────────────────────────────────

const KINDS: DispositionKind[] = ['off_station', 'operations', 'write_off', 'recount'];

function DispositionLots({
  lots,
  onDone,
}: {
  lots: Array<{ key: string; event: EventFacts; row: EventFactsLedgerRow; open: number; out: number }>;
  onDone: () => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<{ lot: (typeof lots)[number]; kind: DispositionKind } | null>(null);
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const units = lots.reduce((a, l) => a + l.open, 0);

  const dispose = useMutation({
    mutationFn: () =>
      movementsApi.dispose(picked!.lot.event.event.id, {
        stockId: picked!.lot.row.stockId,
        kind: picked!.kind,
        quantity: Math.max(1, Math.min(picked!.lot.open, parseInt(qty || '0', 10) || picked!.lot.open)),
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      toast({ tone: 'success', title: t('stock.dispo.savedToast') });
      setPicked(null);
      void qc.invalidateQueries({ queryKey: ['insights'] });
      void qc.invalidateQueries({ queryKey: queryKeys.stockMovements.recent });
      void qc.invalidateQueries({ queryKey: queryKeys.stock.all });
      onDone();
    },
    onError: (e: Error) => toast({ tone: 'error', title: t('stock.dispo.failedToast'), description: e.message }),
  });

  return (
    <section data-testid="disposition-lots" className="rounded-2xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-5">
      <p className="dash-mono text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--color-dash-amber-ink, #B4560A)' }}>
        {t('stock.dispo.kicker')}
      </p>
      <h2 className="mt-1 text-lg font-bold text-[var(--color-fg-primary)]">
        {units > 0 ? t('stock.dispo.head', { units: fmtInt(units), lots: lots.length }) : t('stock.dispo.allClear')}
      </h2>
      <p className="mt-1 max-w-xl text-sm text-[var(--color-fg-muted)]">{t('stock.dispo.sub')}</p>
      <div className="mt-4 space-y-3">
        {lots.map((l) => (
          <div key={l.key} className="rounded-xl border p-4" style={{ borderColor: 'var(--color-dash-amber-border, #E8CFA3)' }}>
            <div className="flex flex-wrap items-start gap-3">
              <span className="dash-mono grid h-9 w-9 shrink-0 place-items-center rounded-full text-[10px] font-bold" style={{ background: 'var(--color-dash-amber-soft, #FDF6EC)', color: 'var(--color-dash-amber-ink, #B4560A)' }}>
                {l.row.name.slice(0, 2).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[var(--color-fg-primary)]">{l.row.name}</p>
                <p className="text-xs text-[var(--color-fg-muted)]">
                  {dayjs(l.event.event.startDate ?? undefined).format('MMM D')} · {l.event.event.name} · {t('stock.dispo.basis', { out: fmtInt(l.out), p: l.out ? Math.round((l.open / l.out) * 1000) / 10 : 0 })}
                </p>
              </div>
              <div className="text-right">
                <div className="dash-mono text-xl font-bold tabular-nums">{fmtInt(l.open)}</div>
                <div className="dash-mono text-[11px] text-[var(--color-fg-muted)]">{l.row.unitCost == null ? t('stock.warehouse.costPending') : fmtUsd(l.open * l.row.unitCost)}</div>
              </div>
              <span className="dash-mono rounded-full px-2 py-1 text-[9px] font-bold uppercase tracking-widest" style={{ background: 'var(--color-dash-amber-soft, #FDF6EC)', color: '#7A5200' }}>
                {t('stock.dispo.needs')}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => { setPicked({ lot: l, kind: k }); setQty(String(l.open)); setNote(''); }}
                  className="rounded-full border border-[var(--color-border-strong)] px-3 py-1.5 text-xs font-bold text-[var(--color-fg-primary)] hover:border-[var(--color-accent-ink)]"
                >
                  {t(`stock.dispo.kind.${k}`)}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-[var(--color-fg-muted)]">{t('stock.dispo.blurb')}</p>
          </div>
        ))}
      </div>

      <Dialog open={!!picked} onOpenChange={(o) => !o && setPicked(null)}>
        <DialogContent className="sm:max-w-md">
          {picked ? (
            <>
              <DialogHeader>
                <DialogTitle>{t(`stock.dispo.kind.${picked.kind}`)} · {picked.lot.row.name}</DialogTitle>
                <DialogDescription>{t(`stock.dispo.kindBlurb.${picked.kind}`)}</DialogDescription>
              </DialogHeader>
              <div className="mt-3 space-y-3">
                <label className="block">
                  <span className="mb-1 block text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
                    {t('stock.dispo.units', { max: picked.lot.open })}
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={picked.lot.open}
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    className="h-10 w-full rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] px-3 text-sm tabular-nums"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">{t('stock.dispo.note')}</span>
                  <input
                    type="text"
                    value={note}
                    maxLength={255}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t('stock.dispo.notePlaceholder')}
                    className="h-10 w-full rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] px-3 text-sm"
                  />
                </label>
                <p className="text-xs text-[var(--color-fg-subtle)]">{t('stock.dispo.appendOnly')}</p>
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setPicked(null)} disabled={dispose.isPending}>{t('common.cancel')}</Button>
                <Button onClick={() => dispose.mutate()} loading={dispose.isPending}>{t('stock.dispo.confirm')}</Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}

// ── Movimientos recientes ────────────────────────────────────────────────────

export function RecentMovements() {
  const t = useT();
  const q = useQuery({ queryKey: queryKeys.stockMovements.recent, queryFn: () => movementsApi.recent(40), staleTime: 30_000 });
  const rows = q.data ?? [];
  // Ocho por página: el libro se consulta, no se lee de arriba abajo. Cuarenta
  // filas seguidas eran un scroll sin fin debajo del catálogo.
  const pager = usePagination(rows, 8);
  return (
    <section data-testid="recent-movements" className="rounded-2xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold text-[var(--color-fg-primary)]">{t('stock.movements.title')}</h2>
        <span className="text-xs text-[var(--color-fg-muted)]">{t('stock.movements.appendOnly')}</span>
      </div>
      {q.isLoading ? (
        <p className="py-6 text-xs text-[var(--color-fg-muted)]">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="py-6 text-sm text-[var(--color-fg-muted)]">{t('stock.movements.empty')}</p>
      ) : (
        <>
        <ul className="mt-2 divide-y divide-[var(--color-border-subtle)]">
          {pager.pageItems.map((m, i) => (
            <li key={`${m.kind}-${m.stockId}-${m.eventId}-${m.at}-${i}`} className="grid grid-cols-[64px_minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-1 py-3">
              <span className={cn('dash-mono text-right text-base font-bold tabular-nums', m.qty < 0 ? 'text-[var(--color-dash-amber-ink,#B4560A)]' : 'text-[var(--color-dash-olive)]')}>
                {m.qty > 0 ? `+${m.qty}` : m.qty}
              </span>
              <span className="min-w-0">
                <span className="font-semibold text-[var(--color-fg-primary)]">{m.item}</span>
                <span className="ml-3 text-xs text-[var(--color-fg-muted)]">
                  {t(`stock.movements.state.${m.from}` as never) || m.from} → {t(`stock.movements.state.${m.to}` as never) || m.to}
                  {m.event ? ` · ${m.event}` : ''}
                </span>
              </span>
              <span className="dash-mono text-[11px] text-[var(--color-fg-subtle)]">{dayjs(m.at).format('MMM D, h:mm A')}</span>
              <span className="col-start-2 text-xs text-[var(--color-fg-muted)]">{m.note ?? t('stock.movements.noNote')}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3">
          <Pagination {...pager} noun={t('stock.movements.noun')} />
        </div>
        </>
      )}
    </section>
  );
}
